'use client';

import React from 'react';
import Link from 'next/link';
import { ChevronLeft, Leaf } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen gradient-bg">
      <div className="fixed -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, #A8C5AD, transparent)' }} />

      <div className="max-w-2xl mx-auto px-5 py-8 pb-16">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link
            href="/profile"
            className="w-9 h-9 rounded-xl bg-white/80 border border-border flex items-center justify-center shadow-card-sm hover:bg-white transition-colors"
          >
            <ChevronLeft size={18} className="text-foreground" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl lavender-gradient flex items-center justify-center shadow-card-sm">
              <Leaf size={14} className="text-white" />
            </div>
            <h1 className="text-lg font-extrabold text-foreground tracking-tight">Privacy Policy</h1>
          </div>
        </div>

        <div className="bg-white/80 rounded-2xl border border-border shadow-card-sm p-6 space-y-6">
          <div>
            <p className="text-xs text-muted-foreground mb-4">Last updated: August 2026</p>
            <p className="text-sm text-foreground leading-relaxed">
              My Little Clingy ("Clingy", "we", "us") is committed to protecting your privacy. This policy explains what data we collect, how we use it, and your rights.
            </p>
          </div>

          <Section title="What We Collect">
            <p>We collect information you provide directly:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 text-sm text-muted-foreground">
              <li>Account information (name, email address)</li>
              <li>Mood logs, journal entries, and reflections you create</li>
              <li>Chat conversations with Clingy</li>
              <li>Sleep and screen time logs</li>
              <li>App preferences and settings</li>
            </ul>
          </Section>

          <Section title="How We Use Your Data">
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>To provide and personalise the Clingy experience</li>
              <li>To allow Clingy to remember context across conversations</li>
              <li>To generate mood charts and wellbeing insights for you</li>
              <li>To send account-related emails (password reset, notifications)</li>
            </ul>
            <p className="mt-2">We do <strong>not</strong> sell your data to third parties. We do not use your personal conversations for AI model training.</p>
          </Section>

          <Section title="Data Storage & Security">
            <p>Your data is stored securely using Supabase (PostgreSQL) with row-level security policies that ensure only you can access your own data. All data is encrypted in transit (TLS) and at rest.</p>
          </Section>

          <Section title="AI Processing">
            <p>Your chat messages are sent to OpenAI's API to generate Clingy's responses. OpenAI processes these messages according to their privacy policy. We do not store your messages with OpenAI beyond the current session context.</p>
          </Section>

          <Section title="Your Rights">
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li><strong>Access:</strong> Request a copy of your data via Settings → Privacy & Data → Export</li>
              <li><strong>Deletion:</strong> Delete your account and all data from Profile → Delete Account</li>
              <li><strong>Correction:</strong> Update your profile information at any time</li>
              <li><strong>Portability:</strong> Export your data in a readable format</li>
            </ul>
          </Section>

          <Section title="Cookies">
            <p>We use session cookies only for authentication. We do not use tracking or advertising cookies.</p>
          </Section>

          <Section title="Contact">
            <p>For privacy questions or data requests, contact us at <a href="mailto:privacy@mylittleclingy.com" className="text-primary hover:underline">privacy@mylittleclingy.com</a>.</p>
          </Section>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link href="/terms" className="text-xs text-primary font-semibold hover:underline">Terms of Service</Link>
          <span className="text-xs text-muted-foreground">·</span>
          <Link href="/safety" className="text-xs text-primary font-semibold hover:underline">Safety</Link>
          <span className="text-xs text-muted-foreground">·</span>
          <Link href="/contact" className="text-xs text-primary font-semibold hover:underline">Contact</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-extrabold text-foreground mb-2">{title}</h2>
      <div className="text-sm text-foreground leading-relaxed space-y-2">{children}</div>
    </div>
  );
}
